// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import SearchableSelect from '../../components/common/SearchableSelect'
import { useTableFunnels, type FunnelColumn } from '../../components/common/TableFunnels'
import { useEffect, useMemo, useState } from 'react'
import { Plus, Users, X, Search, ShieldCheck, Layers } from 'lucide-react'
import * as api from '../../api/client'
import { friendlyError } from '../../api/errors'
import { useAuth } from '../../auth/AuthContext'
import {
  GROUP_TYPES, groupTypeLabel, listGroups, createGroup, getGroup, updateGroup, setGroupMembers, setGroupGrants,
  searchGroupMembers, type GroupRow, type GroupDetail, type GroupType, type MemberCandidate,
} from '../../api/groups'

/**
 * D-RBAC-1 (G-1) — Administration → Groups. Typed security groups: a group is Users, Providers,
 * Facilities, Patients or Roles (chosen at creation), holds members of that type only, and — except
 * patient groups, which are scopes — GIVES roles and permissions to every account it reaches.
 * Everything a member gains here is honoured by the existing permission gates (session materialiser).
 */
const card: React.CSSProperties = { background: 'var(--color-surface, #fff)', border: '1px solid var(--color-border, #e5e7eb)', borderRadius: 12, padding: '14px 16px' }
const th: React.CSSProperties = { textAlign: 'left', padding: '9px 12px', fontWeight: 500, fontSize: 12.5, color: 'var(--color-text-soft, #6b7280)', borderBottom: '1px solid var(--color-border, #e5e7eb)', whiteSpace: 'nowrap' }
const td: React.CSSProperties = { padding: '9px 12px', borderBottom: '1px solid var(--color-border, #eef0f2)', fontSize: 13, verticalAlign: 'middle' }
const btn: React.CSSProperties = { fontSize: 12.5, padding: '6px 11px', border: '1px solid var(--color-border, #e5e7eb)', borderRadius: 8, background: 'transparent', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5 }
const btnPrimary: React.CSSProperties = { ...btn, background: 'var(--color-primary, #185FA5)', color: '#fff', border: 'none', fontWeight: 600 }
const input: React.CSSProperties = { height: 32, padding: '0 10px', border: '1px solid var(--color-border, #e5e7eb)', borderRadius: 8, fontSize: 13, background: '#fff', width: '100%', boxSizing: 'border-box' }
const pill = (bg: string, fg: string): React.CSSProperties => ({ fontSize: 11, padding: '2px 9px', borderRadius: 20, background: bg, color: fg, whiteSpace: 'nowrap', fontWeight: 600 })
const TYPE_COLORS: Record<string, [string, string]> = { user: ['#e0f2fe', '#075985'], provider: ['#ede9fe', '#5b21b6'], patient: ['#fce7f3', '#9d174d'], role: ['#dcfce7', '#166534'] }
const TypePill = ({ t }: { t: string }) => <span style={pill(...(TYPE_COLORS[t] ?? ['#f1f5f9', '#334155']))}>{groupTypeLabel(t)}</span>

// D-UI-2: what each column shows, for its header funnel.
const GROUP_FUNNELS: FunnelColumn<GroupRow>[] = [
  { key: 'name',        label: 'Name',        value: g => g.name },
  { key: 'type',        label: 'Type',        value: g => groupTypeLabel(g.group_type) },
  { key: 'members',     label: 'Members',     value: g => g.member_count },
  { key: 'grants',      label: 'Grants',      value: g => g.group_type === 'patient' ? 'scope' : g.grant_count },
  { key: 'status',      label: 'Status',      value: g => g.is_active ? 'Active' : 'Inactive' },
  { key: 'description', label: 'Description', value: g => g.description, kind: 'text' },
]

export default function GroupsTab() {
  const { me, hasPermission } = useAuth()
  const canWrite = hasPermission('admin.users.deactivate')
  const isSaas = !me?.pharmacy_id
  const [pharmacies, setPharmacies] = useState<{ id: string; name: string }[]>([])
  const [pharmacyId, setPharmacyId] = useState<string>('')
  const [items, setItems] = useState<GroupRow[]>([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [openId, setOpenId] = useState<string | null>(null)

  useEffect(() => {
    if (!isSaas) return
    api.listPharmacies().then(r => { setPharmacies(r.items.map((p: any) => ({ id: p.id, name: p.name }))); if (!pharmacyId && r.items[0]) setPharmacyId(r.items[0].id) }).catch(() => {})
  }, [isSaas])   // eslint-disable-line react-hooks/exhaustive-deps

  const reload = async () => {
    if (isSaas && !pharmacyId) return
    setLoading(true)
    try { setItems((await listGroups(isSaas ? pharmacyId : undefined)).items); setErr(null) }
    catch (e) { setErr(friendlyError(e)) }
    finally { setLoading(false) }
  }
  useEffect(() => { void reload() }, [pharmacyId])   // eslint-disable-line react-hooks/exhaustive-deps

  // The header funnels filter the whole loaded list.
  const fx = useTableFunnels(items, GROUP_FUNNELS)

  return (
    <div style={{ padding: '1.25rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1 }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}><Layers size={18} /> Groups</h2>
          <div style={{ fontSize: 12.5, color: 'var(--color-text-soft, #6b7280)' }}>
            Typed security groups. Give roles and permissions to a set of users, providers or role holders at once; patient groups are scopes.
          </div>
        </div>
        {isSaas && (
          <SearchableSelect value={pharmacyId} onChange={setPharmacyId} ariaLabel="Pharmacy" placeholder="Pharmacy…"
            style={{ width: 260 }} options={pharmacies.map(p => ({ value: p.id, label: p.name }))} />
        )}
        {canWrite && <button style={btnPrimary} onClick={() => setCreating(true)}><Plus size={13} /> New group</button>}
      </div>
      {err && <div role="alert" style={{ ...card, background: '#fef2f2', border: '1px solid #fca5a5', color: '#b91c1c', marginBottom: 12 }}>{err}</div>}

      {fx.chips}
      <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
        <table {...fx.tableProps} style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead><tr><th style={th}>Name {fx.funnel('name')}</th><th style={th}>Type {fx.funnel('type')}</th><th style={{ ...th, textAlign: 'center' }}>Members {fx.funnel('members')}</th><th style={{ ...th, textAlign: 'center' }}>Grants {fx.funnel('grants')}</th><th style={th}>Status {fx.funnel('status')}</th><th style={th}>Description {fx.funnel('description')}</th></tr></thead>
          <tbody>
            {loading && <tr><td colSpan={6} style={{ ...td, color: 'var(--color-text-mute)', padding: 22 }}>Loading groups…</td></tr>}
            {!loading && items.length === 0 && (
              <tr><td colSpan={6} style={{ ...td, textAlign: 'center', color: 'var(--color-text-mute)', padding: 26 }}>
                No groups yet.{canWrite && <> <button style={{ background: 'none', border: 'none', color: 'var(--color-primary, #185FA5)', cursor: 'pointer', textDecoration: 'underline', fontSize: 13 }} onClick={() => setCreating(true)}>Create the first one →</button></>}
              </td></tr>
            )}
            {!loading && items.length > 0 && fx.rows.length === 0 && (
              <tr><td colSpan={6} style={{ ...td, textAlign: 'center', color: 'var(--color-text-mute)', padding: 26 }}>No groups match the filters.</td></tr>
            )}
            {fx.rows.map(g => (
              <tr key={g.id} onClick={() => setOpenId(g.id)} style={{ cursor: 'pointer', opacity: g.is_active ? 1 : .55 }}>
                <td style={{ ...td, fontWeight: 600 }}>{g.name}</td>
                <td style={td}><TypePill t={g.group_type} /></td>
                <td style={{ ...td, textAlign: 'center' }}>{g.member_count}</td>
                <td style={{ ...td, textAlign: 'center' }}>{g.group_type === 'patient' ? <span style={{ color: 'var(--color-text-mute)' }}>scope</span> : g.grant_count}</td>
                <td style={td}>{g.is_active ? <span style={pill('#E1F5EE', '#085041')}>Active</span> : <span style={pill('#F1EFE8', '#444441')}>Inactive</span>}</td>
                <td style={{ ...td, color: 'var(--color-text-soft)', whiteSpace: 'normal' }}>{g.description ?? ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {creating && <CreateGroupModal pharmacyId={isSaas ? pharmacyId : undefined} onClose={() => setCreating(false)} onCreated={id => { setCreating(false); void reload(); setOpenId(id) }} />}
      {openId && <GroupDrawer id={openId} pharmacyId={isSaas ? pharmacyId : undefined} canWrite={canWrite} onClose={() => { setOpenId(null); void reload() }} />}
    </div>
  )
}

function CreateGroupModal({ pharmacyId, onClose, onCreated }: { pharmacyId?: string; onClose: () => void; onCreated: (id: string) => void }) {
  const [type, setType] = useState<GroupType>('user')
  const [name, setName] = useState('')
  const [desc, setDesc] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const t = GROUP_TYPES.find(x => x.value === type)!
  const submit = async () => {
    if (!name.trim()) { setErr('Give the group a name.'); return }
    setBusy(true); setErr(null)
    try { const r = await createGroup({ group_type: type, name: name.trim(), description: desc.trim() || undefined, pharmacy_id: pharmacyId }); onCreated(r.id) }
    catch (e) { setErr(friendlyError(e)) } finally { setBusy(false) }
  }
  return (
    <Overlay onClose={onClose}>
      <h3 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 600 }}>New group</h3>
      <div style={{ fontSize: 12.5, color: 'var(--color-text-soft)', marginBottom: 12 }}>First pick what kind of members the group holds. The type cannot be changed later.</div>
      {err && <div role="alert" style={{ fontSize: 12.5, color: '#991b1b', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '7px 10px', marginBottom: 8 }}>{err}</div>}
      <div style={{ display: 'grid', gap: 6, marginBottom: 12 }}>
        {GROUP_TYPES.map(o => (
          <label key={o.value} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 13, cursor: 'pointer', padding: '6px 8px', borderRadius: 8, background: type === o.value ? 'var(--color-surface-2, #f3f4f6)' : 'transparent' }}>
            <input type="radio" name="group-type" checked={type === o.value} onChange={() => setType(o.value)} style={{ marginTop: 3 }} />
            <span><TypePill t={o.value} /> <span style={{ marginLeft: 6, color: 'var(--color-text-soft)', fontSize: 12 }}>{o.members}</span><br /><span style={{ fontSize: 12, color: 'var(--color-text-mute)' }}>{o.help}</span></span>
          </label>
        ))}
      </div>
      <label style={{ display: 'block', fontSize: 12.5, color: 'var(--color-text-soft)', marginBottom: 8 }}>Name
        <input style={input} value={name} onChange={e => setName(e.target.value)} placeholder={type === 'provider' ? 'e.g. CDI providers' : `e.g. ${t.label} group`} autoFocus /></label>
      <label style={{ display: 'block', fontSize: 12.5, color: 'var(--color-text-soft)', marginBottom: 12 }}>Description (optional)
        <input style={input} value={desc} onChange={e => setDesc(e.target.value)} /></label>
      {type === 'patient' && <div style={{ fontSize: 12, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '7px 10px', marginBottom: 10 }}>A patient group is protected health information. Keep the name neutral (no conditions or drugs).</div>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button style={btn} onClick={onClose}>Cancel</button>
        <button style={btnPrimary} disabled={busy} onClick={() => void submit()}>{busy ? 'Creating…' : 'Create group'}</button>
      </div>
    </Overlay>
  )
}

function GroupDrawer({ id, pharmacyId, canWrite, onClose }: { id: string; pharmacyId?: string; canWrite: boolean; onClose: () => void }) {
  const [d, setD] = useState<GroupDetail | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [tab, setTab] = useState<'members' | 'grants'>('members')
  const reload = () => getGroup(id).then(setD).catch(e => setErr(friendlyError(e)))
  useEffect(() => { void reload() }, [id])   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { const k = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }; document.addEventListener('keydown', k); return () => document.removeEventListener('keydown', k) }, [onClose])

  const toggleActive = async () => {
    if (!d) return
    if (d.group.is_active && !window.confirm(`Deactivate "${d.group.name}"? Every member loses what this group grants on their next request.`)) return
    setBusy(true)
    try { await updateGroup(id, { is_active: !d.group.is_active }); await reload(); setNote(d.group.is_active ? 'Group deactivated.' : 'Group reactivated.') }
    catch (e) { setErr(friendlyError(e)) } finally { setBusy(false) }
  }

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 60, display: 'flex', justifyContent: 'flex-end' }}>
      <aside role="dialog" aria-label="Group" onClick={e => e.stopPropagation()} style={{ width: 640, maxWidth: '96vw', height: '100%', background: '#fff', overflowY: 'auto', boxShadow: '-8px 0 24px rgba(0,0,0,.12)' }}>
        <div style={{ position: 'sticky', top: 0, background: '#fff', borderBottom: '1px solid var(--color-border, #e5e7eb)', padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 10, zIndex: 1 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 15, display: 'flex', alignItems: 'center', gap: 8 }}>{d?.group.name ?? 'Group'} {d && <TypePill t={d.group.group_type} />}{d && !d.group.is_active && <span style={pill('#F1EFE8', '#444441')}>Inactive</span>}</div>
            {d?.group.description && <div style={{ fontSize: 12, color: 'var(--color-text-soft)' }}>{d.group.description}</div>}
          </div>
          {canWrite && d && <button style={btn} disabled={busy} onClick={() => void toggleActive()}>{d.group.is_active ? 'Deactivate' : 'Reactivate'}</button>}
          <button type="button" onClick={onClose} aria-label="Close" style={{ border: 'none', background: 'transparent', cursor: 'pointer' }}><X size={18} /></button>
        </div>
        <div style={{ padding: '12px 18px' }}>
          {err && <div role="alert" style={{ fontSize: 12.5, color: '#991b1b', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '7px 10px', marginBottom: 8 }}>{err}</div>}
          {note && <div style={{ fontSize: 12.5, color: '#166534', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6, padding: '7px 10px', marginBottom: 8 }}>{note}</div>}
          {!d ? <div style={{ fontSize: 13, color: 'var(--color-text-mute)' }}>Loading…</div> : (<>
            <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
              <button style={tab === 'members' ? btnPrimary : btn} onClick={() => setTab('members')}><Users size={13} /> Members ({d.members.length})</button>
              {d.group.group_type !== 'patient' && <button style={tab === 'grants' ? btnPrimary : btn} onClick={() => setTab('grants')}><ShieldCheck size={13} /> Grants ({d.grants.roles.length + d.grants.permissions.length})</button>}
            </div>
            {tab === 'members'
              ? <MembersPanel d={d} pharmacyId={pharmacyId} canWrite={canWrite} onSaved={async n => { await reload(); setNote(n) }} onError={setErr} />
              : <GrantsPanel d={d} canWrite={canWrite} onSaved={async n => { await reload(); setNote(n) }} onError={setErr} />}
          </>)}
        </div>
      </aside>
    </div>
  )
}

function MembersPanel({ d, pharmacyId, canWrite, onSaved, onError }: { d: GroupDetail; pharmacyId?: string; canWrite: boolean; onSaved: (n: string) => void; onError: (e: string) => void }) {
  const type = d.group.group_type
  const [selected, setSelected] = useState<MemberCandidate[]>(d.members.map(m => ({ id: m.id, label: m.label, sub: m.sub })))
  const [q, setQ] = useState('')
  const [results, setResults] = useState<MemberCandidate[]>([])
  const [busy, setBusy] = useState(false)
  const t = GROUP_TYPES.find(x => x.value === type)!
  useEffect(() => { setSelected(d.members.map(m => ({ id: m.id, label: m.label, sub: m.sub }))) }, [d])
  useEffect(() => {
    let cancelled = false
    const h = setTimeout(() => {
      searchGroupMembers(type, q.trim(), pharmacyId).then(r => { if (!cancelled) setResults(r.items) }).catch(e => onError(friendlyError(e)))
    }, 200)
    return () => { cancelled = true; clearTimeout(h) }
  }, [q, type, pharmacyId])   // eslint-disable-line react-hooks/exhaustive-deps
  const selectedIds = useMemo(() => new Set(selected.map(s => s.id)), [selected])
  const dirty = useMemo(() => {
    const a = new Set(d.members.map(m => m.id)); return selected.length !== a.size || selected.some(s => !a.has(s.id))
  }, [selected, d])
  const save = async () => {
    setBusy(true)
    try { const r = await setGroupMembers(d.group.id, selected.map(s => s.id)); onSaved(`Saved ${r.count} ${t.members}.`) }
    catch (e) { onError(friendlyError(e)) } finally { setBusy(false) }
  }
  return (
    <div>
      <div style={{ fontSize: 12.5, color: 'var(--color-text-soft)', marginBottom: 8 }}>This group holds <b>{t.members}</b>. {t.help}</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Add {t.members}</div>
          <div style={{ position: 'relative', marginBottom: 6 }}>
            <Search size={13} style={{ position: 'absolute', left: 9, top: 10, color: 'var(--color-text-mute)' }} />
            <input value={q} onChange={e => setQ(e.target.value)} placeholder={`Search ${t.members}…`} aria-label={`Search ${t.members}`} style={{ ...input, paddingLeft: 28 }} disabled={!canWrite} />
          </div>
          <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid var(--color-border, #e5e7eb)', borderRadius: 8 }}>
            {results.filter(r => !selectedIds.has(r.id)).map(r => (
              <button key={r.id} type="button" disabled={!canWrite} onClick={() => setSelected(s => [...s, r])}
                style={{ display: 'block', width: '100%', textAlign: 'left', padding: '7px 10px', border: 'none', borderBottom: '1px solid var(--color-border, #eef0f2)', background: 'transparent', cursor: 'pointer', fontSize: 13 }}>
                <span style={{ fontWeight: 600 }}>{r.label}</span>{r.sub && <span style={{ color: 'var(--color-text-mute)', marginLeft: 8, fontSize: 12 }}>{r.sub}</span>}
              </button>
            ))}
            {results.filter(r => !selectedIds.has(r.id)).length === 0 && <div style={{ padding: 12, fontSize: 12.5, color: 'var(--color-text-mute)' }}>{q ? 'No matches.' : 'Type to search, or pick from the list.'}</div>}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Members ({selected.length})</div>
          <div style={{ maxHeight: 362, overflowY: 'auto', border: '1px solid var(--color-border, #e5e7eb)', borderRadius: 8 }}>
            {selected.map(m => (
              <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', borderBottom: '1px solid var(--color-border, #eef0f2)', fontSize: 13 }}>
                <span style={{ flex: 1 }}><span style={{ fontWeight: 600 }}>{m.label}</span>{m.sub && <span style={{ color: 'var(--color-text-mute)', marginLeft: 8, fontSize: 12 }}>{m.sub}</span>}</span>
                {canWrite && <button type="button" aria-label={`Remove ${m.label}`} onClick={() => setSelected(s => s.filter(x => x.id !== m.id))} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--color-text-mute)' }}><X size={14} /></button>}
              </div>
            ))}
            {selected.length === 0 && <div style={{ padding: 12, fontSize: 12.5, color: 'var(--color-text-mute)' }}>No members yet.</div>}
          </div>
        </div>
      </div>
      {canWrite && <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
        <button style={btnPrimary} disabled={!dirty || busy} onClick={() => void save()}>{busy ? 'Saving…' : 'Save members'}</button>
      </div>}
    </div>
  )
}

function GrantsPanel({ d, canWrite, onSaved, onError }: { d: GroupDetail; canWrite: boolean; onSaved: (n: string) => void; onError: (e: string) => void }) {
  const [roles, setRoles] = useState<{ id: string; name: string; is_system: boolean }[]>([])
  const [perms, setPerms] = useState<{ key: string; display_name?: string; category?: string | null }[]>([])
  const [roleIds, setRoleIds] = useState<string[]>(d.grants.roles.map(r => r.id))
  const [permKeys, setPermKeys] = useState<string[]>(d.grants.permissions)
  const [q, setQ] = useState('')
  const [busy, setBusy] = useState(false)
  useEffect(() => { setRoleIds(d.grants.roles.map(r => r.id)); setPermKeys(d.grants.permissions) }, [d])
  useEffect(() => {
    api.rbacListRoles().then(r => setRoles(r.items)).catch(e => onError(friendlyError(e)))
    api.rbacListPermissions().then(r => setPerms(r.items as any)).catch(e => onError(friendlyError(e)))
  }, [])   // eslint-disable-line react-hooks/exhaustive-deps
  const dirty = roleIds.length !== d.grants.roles.length || roleIds.some(id => !d.grants.roles.some(r => r.id === id))
    || permKeys.length !== d.grants.permissions.length || permKeys.some(k => !d.grants.permissions.includes(k))
  const save = async () => {
    setBusy(true)
    try { const r = await setGroupGrants(d.group.id, { role_ids: roleIds, permissions: permKeys }); onSaved(`Saved ${r.count} grant(s). ${r.sessions_refreshed} live session(s) refreshed.`) }
    catch (e) { onError(friendlyError(e)) } finally { setBusy(false) }
  }
  const byCat = useMemo(() => {
    const n = q.trim().toLowerCase()
    const m = new Map<string, typeof perms>()
    for (const p of perms) {
      if (n && !(p.key.toLowerCase().includes(n) || (p.display_name ?? '').toLowerCase().includes(n))) continue
      const c = p.category ?? 'other'; if (!m.has(c)) m.set(c, []); m.get(c)!.push(p)
    }
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]))
  }, [perms, q])
  const toggle = (arr: string[], set: (v: string[]) => void, v: string) => set(arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v])
  return (
    <div>
      <div style={{ fontSize: 12.5, color: 'var(--color-text-soft)', marginBottom: 10 }}>
        Everything ticked here is added to what each member already has. Roles expand to their permissions. Members keep their own scopes; a group never widens a scope.
      </div>
      <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Roles</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
        {roles.map(r => (
          <label key={r.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, padding: '4px 10px', border: '1px solid var(--color-border, #e5e7eb)', borderRadius: 20, background: roleIds.includes(r.id) ? '#dcfce7' : '#fff', cursor: canWrite ? 'pointer' : 'default' }}>
            <input type="checkbox" checked={roleIds.includes(r.id)} disabled={!canWrite} onChange={() => toggle(roleIds, setRoleIds, r.id)} /> {r.name}
          </label>
        ))}
        {roles.length === 0 && <span style={{ fontSize: 12.5, color: 'var(--color-text-mute)' }}>Loading roles…</span>}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <div style={{ fontSize: 12, fontWeight: 600, flex: 1 }}>Permissions ({permKeys.length} selected)</div>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Filter permissions…" aria-label="Filter permissions" style={{ ...input, width: 220 }} />
      </div>
      <div style={{ maxHeight: 300, overflowY: 'auto', border: '1px solid var(--color-border, #e5e7eb)', borderRadius: 8, padding: '4px 0' }}>
        {byCat.map(([cat, list]) => (
          <div key={cat}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: .5, textTransform: 'uppercase', color: 'var(--color-text-mute)', padding: '6px 10px 2px' }}>{cat}</div>
            {list.map(p => (
              <label key={p.key} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '4px 10px', fontSize: 12.5, cursor: canWrite ? 'pointer' : 'default' }}>
                <input type="checkbox" checked={permKeys.includes(p.key)} disabled={!canWrite} onChange={() => toggle(permKeys, setPermKeys, p.key)} />
                <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>{p.key}</span>
                {p.display_name && <span style={{ color: 'var(--color-text-mute)' }}>— {p.display_name}</span>}
              </label>
            ))}
          </div>
        ))}
        {byCat.length === 0 && <div style={{ padding: 12, fontSize: 12.5, color: 'var(--color-text-mute)' }}>No permissions match.</div>}
      </div>
      {canWrite && <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
        <button style={btnPrimary} disabled={!dirty || busy} onClick={() => void save()}>{busy ? 'Saving…' : 'Save grants'}</button>
      </div>}
    </div>
  )
}

function Overlay({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, padding: '1.25rem', width: 520, maxWidth: '92vw', maxHeight: '90vh', overflow: 'auto' }}>{children}</div>
    </div>
  )
}
